import React from 'react';
import { Box, Container, Typography, Chip } from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { motion } from 'framer-motion';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const technicalSpecs = {
  security: [
    'End-to-end encryption',
    'Multi-factor authentication',
    'Role-based access control',
    'Audit logging',
    'Data encryption at rest',
    'Secure API endpoints',
  ],
  compliance: [
    'CJIS v6.0 compliant',
    'FedRAMP authorized',
    'SOC 2 Type II certified',
    'NIST framework',
    'Zero-knowledge architecture',
    'Regular security audits',
  ],
  integration: [
    'Modern database connectivity',
    'REST API support',
    'Custom data sources',
    'Webhook notifications',
    'SSO integration',
    'Export capabilities',
  ],
  infrastructure: [
    'Cloud-native architecture',
    'Auto-scaling',
    '99.9% uptime SLA',
    'Global CDN',
    'Disaster recovery',
    'Regular backups',
  ],
};

const TechnicalSection: React.FC = () => {
  return (
    <Box
      id="technical"
      sx={{
        py: 10,
        backgroundColor: '#000000',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              mb: 2,
              fontSize: { xs: '2rem', md: '2.25rem' },
            }}
          >
            Technical Capabilities
          </Typography>
          <Typography
            variant="body1"
            align="center"
            sx={{
              mb: 6,
              color: '#9CA3AF',
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Enterprise-grade security and compliance built for government agencies
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          {Object.entries(technicalSpecs).map(([category, items], index) => (
            <Grid item xs={12} sm={6} md={3} key={category}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Box
                  sx={{
                    backgroundColor: '#0A0A0A',
                    border: '1px solid #1A1A1A',
                    borderRadius: 3,
                    p: 3,
                    height: '100%',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      color: '#1D9BF0',
                      textTransform: 'capitalize',
                      fontWeight: 600,
                    }}
                  >
                    {category}
                  </Typography>
                  
                  {items.map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                      }}
                    >
                      <CheckCircleIcon
                        sx={{
                          fontSize: 20,
                          color: '#1D9BF0',
                          mr: 1,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#C7CDD3',
                        }}
                      >
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Box
            sx={{
              mt: 6,
              p: 4,
              backgroundColor: '#0A0A0A',
              border: '1px solid #1A1A1A',
              borderRadius: 3,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                mb: 2,
                color: '#E7E9EA',
                fontWeight: 600,
              }}
            >
              Built for Scale and Security
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: '#C7CDD3',
                maxWidth: '800px',
                mx: 'auto',
              }}
            >
              VECTOR's architecture is designed to handle millions of records while maintaining 
              sub-second response times. Our platform scales seamlessly from small departments 
              to large federal agencies without compromising performance or security.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Chip label="AWS Infrastructure" sx={{ backgroundColor: '#1D9BF0', color: '#FFF' }} />
              <Chip label="Kubernetes" sx={{ backgroundColor: '#1D9BF0', color: '#FFF' }} />
              <Chip label="PostgreSQL" sx={{ backgroundColor: '#1D9BF0', color: '#FFF' }} />
              <Chip label="Redis Cache" sx={{ backgroundColor: '#1D9BF0', color: '#FFF' }} />
              <Chip label="ElasticSearch" sx={{ backgroundColor: '#1D9BF0', color: '#FFF' }} />
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default TechnicalSection;